import React, { useCallback, useRef, useState } from 'react';

import Lottie from 'lottie-react';
import PropTypes from 'prop-types';

import plusMinus from '../img/lottie/plus-minus.json';

const Question = React.memo(({ question, answer, openedQuestion, setOpenedQuestion }) => {
  const lottieRef = useRef(null);
  const isOpen = openedQuestion === question;

  const toggleQuestion = useCallback(() => {
    if (isOpen) {
      lottieRef.current?.setDirection(-1);
      lottieRef.current?.play();
    } else {
      lottieRef.current?.setDirection(1);
      lottieRef.current?.play();
    }
    setOpenedQuestion(isOpen ? '' : question);
  }, [isOpen, question, setOpenedQuestion]);

  return (
    <div
      className={`group rounded-[20px] p-[26px] transition-all duration-1000 ${
        isOpen ? 'outline outline-neutral-200 ' : 'outline outline-transparent'
      }`}
    >
      <div
        className="mb-[10px] flex cursor-pointer items-center justify-between text-[17px] text-lg font-bold leading-[30px] text-neutral-950"
        onClick={toggleQuestion}
      >
        {question}
        <Lottie
          lottieRef={lottieRef}
          animationData={plusMinus}
          className="mt-[4px] h-[18px] w-[50px] pl-[8px]"
          autoplay={false}
          loop={false}
        />
      </div>
      <div
        className="overflow-hidden text-[17px] leading-[30px] text-[#666666] transition-all delay-200 duration-1000"
        style={{
          maxHeight: isOpen ? '500px' : '0',
          opacity: isOpen ? 1 : 0,
        }}
      >
        {isOpen && answer}
      </div>
    </div>
  );
});

Question.propTypes = {
  question: PropTypes.string.isRequired,
  answer: PropTypes.string.isRequired,
  openedQuestion: PropTypes.string.isRequired,
  setOpenedQuestion: PropTypes.func.isRequired,
};

export const FAQ = React.memo(() => {
  const [openedQuestion, setOpenedQuestion] = useState('');

  return (
    <div id="faq" className="mx-auto mb-[64px] flex max-w-[1260px] flex-col gap-12 px-[20px] lg:flex-row lg:px-[70px]">
      <div className="lg:max-w-[504px]">
        <h2 className="mb-3 text-center text-[17px] text-sm font-bold leading-[30px] text-primary lg:text-start">
          FAQ
        </h2>
        <h1 className="mb-[18px] text-center font-title text-[32px] font-bold leading-[41px] tracking-[-1.5px] md:text-[54.03px]  md:leading-[67.2px] lg:text-start">
          Frequently Asked Questions
        </h1>
        <p className="text-[17px] leading-[30px] text-[#666666]">
          For any unanswered questions, reach out to our support team via{' '}
          <a
            className="text-[17px] leading-[30px] transition-all duration-200 hover:text-primary "
            href="https://discord.com/invite/ZCj5hFv8xV"
            target="_blank"
            rel="noreferrer"
          >
            Discord
          </a>
          . We’ll respond as soon as possible to assist you.
        </p>
      </div>
      <div className="lg:w-[560px]">
        <section className="flex flex-col">
          {[
            {
              question: 'What is Frontend AI?',
              answer:
                'Frontend AI is an online visual studio and code editor that lives in your browser. It is built for developers who value efficiency and control. With AI-powered precision and your skills, you’ll save time, reduce errors, and deliver interfaces that impress.',
            },
            {
              question: 'How is Frontend AI different from other tools?',
              answer:
                "Frontend AI goes beyond chatbots and suggestions by combining AI with a powerful visual studio. With its real-time theme changing, drag-and-drop and inline editing, you can tweak your components to perfection. It's the one and only AI tool build with frontend developers in mind.",
            },
            {
              question: 'Is Frontend AI mobile-friendly?',
              answer:
                'Frontend AI helps you make your designs mobile-friendly by providing responsive tips and showing you how your work looks across different screen sizes. It empowers you to customize layouts for the best results instead of forcing one-size-fits-all solutions, giving you confidence that your interfaces will work perfectly for your users.',
            },
            {
              question: 'Can I use Frontend AI for free?',
              answer: 'Yes! Frontend AI can be used for free for as long as you need.',
            },
          ].map(({ question, answer }) => (
            <Question
              key={question}
              question={question}
              answer={answer}
              openedQuestion={openedQuestion}
              setOpenedQuestion={setOpenedQuestion}
            />
          ))}
        </section>
      </div>
    </div>
  );
});
